/**
 * The audit of a session's change (`audit`, §6.3, §5.27) — the clamp the
 * application settings, a workspace's override and an organization's default
 * all go through, and the line each surface prints for it. One object, sparse
 * in a workspace's document as `autoLand` is: an older install refuses a key it
 * has never heard of, and this one is absent until it is answered.
 */
import { DEFAULT_SETTINGS } from './defaults';
import { effortsFor, MODEL_IDS, MODELS } from './models';
import { oneOf } from './primitives';
import { AUTO_AUDITS, BLOCK_FINDINGS, BLOCK_RULES, type AuditSettings } from './types';

/**
 * A model the grammar has, or `''` — Airlock's own pick for the map (Sonnet at
 * low, Terra where Codex is the only agent); an effort only one that model
 * takes, else none.
 */
export function audit(input: unknown, fallback: AuditSettings): AuditSettings {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Partial<AuditSettings>;
  const auto = (oneOf(raw.auto, AUTO_AUDITS) as AuditSettings['auto']) ?? fallback.auto;
  const asked = raw.mapModel === '' ? '' : (oneOf(raw.mapModel, MODEL_IDS) ?? fallback.mapModel);
  const efforts = asked ? effortsFor(asked).map((one) => one.value) : [];
  const effort = oneOf(raw.mapEffort, efforts) ?? (efforts.includes(fallback.mapEffort) ? fallback.mapEffort : '');
  return {
    auto,
    mapModel: asked,
    mapEffort: asked ? effort : '',
    blockRules: (oneOf(raw.blockRules, BLOCK_RULES) as AuditSettings['blockRules']) ?? fallback.blockRules,
    blockFindings: (oneOf(raw.blockFindings, BLOCK_FINDINGS) as AuditSettings['blockFindings']) ?? fallback.blockFindings
  };
}

/** A workspace's or an organization's answer: anything but an object is no answer. */
export function auditOverride(input: unknown): AuditSettings | null {
  return typeof input === 'object' && input !== null && !Array.isArray(input)
    ? audit(input, DEFAULT_SETTINGS.audit)
    : null;
}

/** `High and Medium · map on Opus · high · holds on enforced MUST, High`. */
export function auditSummary(settings: AuditSettings): string {
  const auto = settings.auto === 'all' ? 'Every finding' : settings.auto === 'serious' ? 'High and Medium' : 'Off';
  const model = MODELS.find((one) => one.value === settings.mapModel)?.label;
  const map = model ? `map on ${model}${settings.mapEffort ? ` · ${settings.mapEffort}` : ''}` : 'map automatic';
  const rules = { enforced: 'enforced MUST', must: 'any MUST', should: 'MUST and SHOULD', none: 'no rule' }[
    settings.blockRules
  ];
  const findings = { high: 'High', medium: 'High and Medium', none: 'no finding' }[settings.blockFindings];
  return `${auto} · ${map} · holds on ${rules}, ${findings}`;
}
